const TEXT_ENCODER = new TextEncoder()
const TEXT_DECODER = new TextDecoder()

function arrayBufferToBase64(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer)
  let binary = ""
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i])
  }
  return btoa(binary)
}

function base64ToArrayBuffer(base64: string): ArrayBuffer {
  const binary = atob(base64)
  const bytes = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i)
  }
  return bytes.buffer
}

async function deriveKey(
  passphrase: string,
  salt: Uint8Array,
): Promise<CryptoKey> {
  const baseKey = await crypto.subtle.importKey(
    "raw",
    TEXT_ENCODER.encode(passphrase),
    "PBKDF2",
    false,
    ["deriveKey"],
  )
  return crypto.subtle.deriveKey(
    {
      name: "PBKDF2",
      salt: new Uint8Array(salt),
      iterations: 100000,
      hash: "SHA-256",
    },
    baseKey,
    { name: "AES-GCM", length: 256 },
    false,
    ["encrypt", "decrypt"],
  )
}

export async function encryptString(
  plaintext: string,
  passphrase: string,
): Promise<string> {
  const salt = crypto.getRandomValues(new Uint8Array(16))
  const iv = crypto.getRandomValues(new Uint8Array(12))
  const key = await deriveKey(passphrase, salt)
  const ciphertext = await crypto.subtle.encrypt(
    { name: "AES-GCM", iv },
    key,
    TEXT_ENCODER.encode(plaintext),
  )
  const payload = [
    "enc",
    "v1",
    arrayBufferToBase64(salt.buffer),
    arrayBufferToBase64(iv.buffer),
    arrayBufferToBase64(ciphertext),
  ].join(":")
  return payload
}

export async function decryptString(
  payload: string,
  passphrase: string,
): Promise<string> {
  // Add null/undefined/empty checks
  if (!payload || typeof payload !== "string" || payload.trim() === "") {
    throw new Error("Invalid payload: payload is null, undefined, or empty")
  }

  if (
    !passphrase ||
    typeof passphrase !== "string" ||
    passphrase.trim() === ""
  ) {
    throw new Error(
      "Invalid passphrase: passphrase is null, undefined, or empty",
    )
  }

  if (!isEncryptedString(payload)) {
    throw new Error("Not an encrypted payload")
  }

  const parts = payload.split(":")
  if (parts.length !== 5) {
    throw new Error("Invalid encrypted payload format")
  }

  const [, , saltB64, ivB64, dataB64] = parts

  // Validate base64 parts are not empty
  if (!saltB64 || !ivB64 || !dataB64) {
    throw new Error("Invalid encrypted payload: missing components")
  }

  try {
    const salt = new Uint8Array(base64ToArrayBuffer(saltB64))
    const iv = new Uint8Array(base64ToArrayBuffer(ivB64))
    const key = await deriveKey(passphrase, salt)
    const plaintextBuffer = await crypto.subtle.decrypt(
      { name: "AES-GCM", iv },
      key,
      base64ToArrayBuffer(dataB64),
    )
    return TEXT_DECODER.decode(plaintextBuffer)
  } catch (error) {
    throw new Error(
      `Decryption failed: ${error instanceof Error ? error.message : "Unknown error"}`,
    )
  }
}

export function isEncryptedString(value: unknown): value is string {
  return (
    typeof value === "string" &&
    value.trim() !== "" &&
    value.startsWith("enc:v1:") &&
    value.split(":").length === 5
  )
}

export function generateSecretToken(length = 32) {
  const characters =
    "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789"
  let token = ""
  for (let i = 0; i < length; i++) {
    const randomIndex = Math.floor(Math.random() * characters.length)
    token += characters[randomIndex]
  }
  return token
}
